import React from 'react';
import { View, Text, Pressable, ScrollView, Platform } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { Ionicons } from '@expo/vector-icons';

export interface Tab {
  key: string;
  label: string;
  icon?: keyof typeof Ionicons.glyphMap;
  badge?: number;
}

export interface TabBarProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabKey: string) => void;
  style?: any;
}

export function TabBar({ tabs, activeTab, onTabChange, style }: TabBarProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const { isMobile, isSmallMobile } = useResponsive();

  const tabContent = tabs.map((tab) => {
    const isActive = tab.key === activeTab;
    
    return (
      <Pressable
        key={tab.key}
        onPress={() => onTabChange(tab.key)}
        style={({ pressed }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          paddingHorizontal: isMobile ? utils.spacing.md : utils.spacing.lg,
          paddingVertical: utils.spacing.sm,
          borderRadius: utils.radius.md,
          backgroundColor: isActive 
            ? tokens.color.primary 
            : pressed 
              ? addColorOpacity(tokens.color.primary, 0.1)
              : 'transparent',
          gap: utils.spacing.xs,
          minWidth: isMobile ? 80 : 100,
          ...Platform.select({
            web: {
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              userSelect: 'none',
            } as any,
          }),
        })}
      >
        {tab.icon && (
          <Ionicons
            name={tab.icon}
            size={isMobile ? 16 : 18}
            color={isActive ? tokens.color.onPrimary : addColorOpacity(tokens.color.onSurface, 0.7)}
          />
        )}
        <Text
          style={{
            fontSize: isMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
            fontWeight: isActive ? utils.typography.weights.semibold : utils.typography.weights.medium,
            color: isActive ? tokens.color.onPrimary : addColorOpacity(tokens.color.onSurface, 0.7),
            fontFamily: utils.typography.body.fontFamily,
          }}
        >
          {tab.label}
        </Text>
        {tab.badge !== undefined && tab.badge > 0 && (
          <View
            style={{
              minWidth: 18,
              height: 18,
              borderRadius: 9,
              backgroundColor: isActive 
                ? addColorOpacity(tokens.color.onPrimary, 0.2)
                : tokens.color.error,
              alignItems: 'center',
              justifyContent: 'center',
              paddingHorizontal: 4,
            }}
          >
            <Text
              style={{
                fontSize: 10,
                fontWeight: utils.typography.weights.bold,
                color: isActive ? tokens.color.onPrimary : tokens.color.onError,
              }}
            >
              {tab.badge > 99 ? '99+' : tab.badge}
            </Text>
          </View>
        )}
      </Pressable>
    );
  });

  // On mobile, use horizontal scroll
  if (isMobile) {
    return (
      <View
        style={[
          {
            backgroundColor: tokens.color.surface,
            borderRadius: utils.radius.lg,
            padding: utils.spacing.xs,
            marginBottom: utils.spacing.lg,
            borderWidth: 1,
            borderColor: tokens.color.border,
          },
          style,
        ]}
      >
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            flexDirection: 'row',
            gap: utils.spacing.xs,
          }}
        >
          {tabContent}
        </ScrollView>
      </View>
    );
  }

  // On desktop, use horizontal scroll (allows scrolling when many tabs)
  return (
    <View
      style={[
        {
          backgroundColor: tokens.color.surface,
          borderRadius: utils.radius.lg,
          padding: utils.spacing.xs,
          marginBottom: utils.spacing.lg,
          borderWidth: 1,
          borderColor: tokens.color.border,
          ...Platform.select({
            web: {
              boxShadow: `0 2px 8px ${addColorOpacity('#000', 0.06)}`,
            } as any,
          }),
        },
        style,
      ]}
    >
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          flexDirection: 'row',
          gap: utils.spacing.xs,
        }}
      >
        {tabContent}
      </ScrollView>
    </View>
  );
}

